import React, { useState, useEffect } from "react";
import TechSoftFooter from "../../components/common/TechSoftFooter";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  PhotoCamera as PhotoCameraIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  atualizarEmpresa,
  atualizarImagemEmpresa,
  getImagemEmpresaUrl,
  buscarEmpresaPorId,
} from "../../services/empresa";
import { obterIdEmpresa } from "../../utils/empresa";

interface EmpresaData {
  id: number;
  nome: string;
  cnpj: string;
  email: string;
  telefone?: string;
  //endereco?: string;
  imagem?: string;
}

// Função para formatar CNPJ
const formatarCNPJ = (cnpj: string): string => {
  if (!cnpj) return "";
  // Remove caracteres não numéricos
  const numeros = cnpj.replace(/\D/g, "");
  // Aplica a máscara XX.XXX.XXX/XXXX-XX
  return numeros.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5",
  );
};

// Função para formatar Telefone
const formatarTelefone = (value: string): string => {
  const numbers = value.replace(/\D/g, "");

  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 6) {
    return numbers.replace(/^(\d{2})(\d{0,4})$/, "($1) $2");
  } else if (numbers.length <= 10) {
    // Telefone fixo: (XX) XXXX-XXXX
    return numbers.replace(/^(\d{2})(\d{4})(\d{0,4})$/, "($1) $2-$3");
  } else {
    // Celular: (XX) XXXXX-XXXX
    return numbers
      .slice(0, 11)
      .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  }
};

interface AlterarEmailForm {
  emailAtual: string;
  novoEmail: string;
  confirmarEmail: string;
}

interface AlterarSenhaForm {
  senhaAtual: string;
  novaSenha: string;
  confirmarSenha: string;
}

const ConfiguracoesEmpresa: React.FC = () => {
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState<EmpresaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error" | "";
    text: string;
  }>({
    type: "",
    text: "",
  });

  // Estados para campos editáveis da empresa
  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [telefoneEmpresa, setTelefoneEmpresa] = useState("");
  //const [enderecoEmpresa, setEnderecoEmpresa] = useState("");

  // Estados para formulários
  const [emailForm, setEmailForm] = useState<AlterarEmailForm>({
    emailAtual: "",
    novoEmail: "",
    confirmarEmail: "",
  });

  const [senhaForm, setSenhaForm] = useState<AlterarSenhaForm>({
    senhaAtual: "",
    novaSenha: "",
    confirmarSenha: "",
  });

  // Estados para mostrar/ocultar senhas
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  // Estados para imagem
  const [imagemSelecionada, setImagemSelecionada] = useState<File | null>(null);
  const [previewImagem, setPreviewImagem] = useState<string>("");

  // Carregar dados da empresa do backend
  useEffect(() => {
    const carregarDadosEmpresa = async () => {
      const empresaId = obterIdEmpresa();

      if (!empresaId) {
        setMessage({
          type: "error",
          text: "Sessão inválida. Faça login novamente.",
        });
        return;
      }

      try {
        setLoading(true);

        // Buscar dados do backend
        const response = await buscarEmpresaPorId(empresaId);

        if (!response) {
          setMessage({
            type: "error",
            text: "Não foi possível carregar os dados da empresa.",
          });
          return;
        }

        // A resposta já é um objeto ApiResponse com {data, success, statusCode}
        const empresaData = (response as any).data || response;

        const dadosCompletos: EmpresaData = {
          id: empresaData.id,
          nome: empresaData.nome,
          cnpj: empresaData.cnpj,
          email: empresaData.email,
          telefone: empresaData.telefone || "",
          imagem: "",
        };

        setEmpresa(dadosCompletos);
        setNomeEmpresa(empresaData.nome || "");
        setTelefoneEmpresa(formatarTelefone(empresaData.telefone || ""));
        setEmailForm((prev: AlterarEmailForm) => ({
          ...prev,
          emailAtual: empresaData.email,
        }));

        // Carregar imagem do backend
        try {
          const urlImagem = `${getImagemEmpresaUrl(
            empresaData.id,
          )}?t=${Date.now()}`;
          setPreviewImagem(urlImagem);
        } catch (error) {
          setPreviewImagem("");
        }
      } catch (error) {
        setMessage({
          type: "error",
          text: "Erro ao carregar dados da empresa. Tente novamente.",
        });
      } finally {
        setLoading(false);
      }
    };

    carregarDadosEmpresa();
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEmailForm((prev: AlterarEmailForm) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSenhaForm((prev: AlterarSenhaForm) => ({
      ...prev,
      [name]: value,
    }));
  };

  const verificarSuporteWebP = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const webpTestImage =
        "data:image/webp;base64,UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==";
      const img = new Image();
      img.onload = () => resolve(img.width === 1 && img.height === 1);
      img.onerror = () => resolve(false);
      img.src = webpTestImage;
    });
  };

  const otimizarImagem = (file: File): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = async () => {
        const maxWidth = 212; // 250 - 15% = 212
        const maxHeight = 212; // Quadrado para ficar melhor no círculo

        let outputFormat = "image/jpeg";
        let outputQuality = 0.85;

        if (file.type === "image/webp") {
          const suportaWebP = await verificarSuporteWebP();
          if (suportaWebP) {
            outputFormat = "image/webp";
            outputQuality = 0.8;
          }
        }

        canvas.width = maxWidth;
        canvas.height = maxHeight;

        if (ctx) {
          // Preencher fundo branco para evitar transparências
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, maxWidth, maxHeight);

          const imgAspect = img.width / img.height;
          const containerAspect = maxWidth / maxHeight; // 212/212 = 1.0 (quadrado)

          const isVeryNarrow = imgAspect < 0.8; // Imagem muito mais alta que larga
          const isVeryWide = imgAspect > 2.5; // Imagem muito mais larga que alta

          if (isVeryNarrow || isVeryWide) {
            // Para imagens extremas: usar estratégia "cover" - preencher container completamente
            let sourceX = 0,
              sourceY = 0,
              sourceWidth = img.width,
              sourceHeight = img.height;

            if (imgAspect > containerAspect) {
              // Imagem mais larga - cortar laterais para caber na altura
              sourceWidth = img.height * containerAspect;
              sourceX = (img.width - sourceWidth) / 2;
            } else {
              // Imagem mais alta/estreita - cortar topo/base para caber na largura
              sourceHeight = img.width / containerAspect;
              sourceY = (img.height - sourceHeight) / 2;
            }

            ctx.drawImage(
              img,
              sourceX,
              sourceY,
              sourceWidth,
              sourceHeight,
              0,
              0,
              maxWidth,
              maxHeight,
            );
          } else {
            // Para imagens normais: usar estratégia "contain" - preservar tudo
            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgAspect > containerAspect) {
              drawWidth = maxWidth;
              drawHeight = maxWidth / imgAspect;
              offsetX = 0;
              offsetY = (maxHeight - drawHeight) / 2;
            } else {
              drawHeight = maxHeight;
              drawWidth = maxHeight * imgAspect;
              offsetX = (maxWidth - drawWidth) / 2;
              offsetY = 0;
            }

            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          }
          const dataUrl = canvas.toDataURL(outputFormat, outputQuality);
          resolve(dataUrl);
        } else {
          reject(new Error("Erro ao processar imagem"));
        }
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImagemChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    if (arquivo) {
      // Validar tipo de arquivo (incluindo WebP nativo com fallback)
      const tiposPermitidos = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!tiposPermitidos.includes(arquivo.type)) {
        setMessage({
          type: "error",
          text: "Apenas arquivos JPEG, PNG, GIF e WebP são permitidos.",
        });
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (arquivo.size > 5 * 1024 * 1024) {
        setMessage({ type: "error", text: "A imagem deve ter no máximo 5MB." });
        return;
      }

      try {
        setMessage({ type: "", text: "Redimensionando imagem..." });

        const imagemOtimizada = await otimizarImagem(arquivo);
        const response = await fetch(imagemOtimizada);
        const blob = await response.blob();
        const arquivoOtimizado = new File([blob], arquivo.name, {
          type: "image/jpeg",
        });

        setImagemSelecionada(arquivoOtimizado);
        setPreviewImagem(imagemOtimizada);
        setMessage({
          type: "success",
          text: "Imagem otimizada com sucesso! Redimensionamento inteligente aplicado.",
        });
      } catch (error) {
        console.error("❌ Erro ao otimizar imagem:", error);
        setMessage({
          type: "error",
          text: "Erro ao processar imagem. Tente novamente.",
        });
      }
    }
  };

  const handleSalvarImagem = async () => {
    if (!imagemSelecionada) {
      setMessage({ type: "error", text: "Selecione uma imagem primeiro." });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const urlImagem = previewImagem;

      if (empresa) {
        // Salvar no backend usando o endpoint específico PUT /empresas/{id}/imagem
        await atualizarImagemEmpresa(empresa.id, urlImagem);

        // Salvar no localStorage
        localStorage.setItem(`empresaImagem_${empresa.id}`, urlImagem);
        localStorage.removeItem("empresaImagem");
      }

      // Atualizar estado local
      if (empresa) {
        setEmpresa({ ...empresa, imagem: urlImagem });
      }

      setImagemSelecionada(null);

      // Atualizar preview com URL do backend
      if (empresa) {
        const urlImagemBackend = `${getImagemEmpresaUrl(
          empresa.id,
        )}?t=${Date.now()}`;
        setPreviewImagem(urlImagemBackend);
      }

      setMessage({
        type: "success",
        text: "Imagem salva com sucesso! Atualizando painel de vagas...",
      });

      // Forçar atualização do painel de vagas após 1 segundo
      setTimeout(() => {
        // Disparar evento customizado para notificar outras páginas
        if (empresa) {
          window.dispatchEvent(
            new CustomEvent("empresaImagemAtualizada", {
              detail: { empresaId: empresa.id, novaImagem: urlImagem },
            }),
          );
        }

        setMessage({
          type: "success",
          text: "Imagem salva e painel atualizado com sucesso!",
        });
      }, 1000);
    } catch (error: any) {
      console.error("Erro ao salvar imagem:", error);
      setMessage({
        type: "error",
        text: "Erro ao salvar imagem. Tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSalvarAlteracoes = async () => {
    if (!empresa) {
      setMessage({ type: "error", text: "Dados da empresa não encontrados." });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // Validações
      if (!nomeEmpresa || nomeEmpresa.trim() === "") {
        setMessage({
          type: "error",
          text: "O nome da empresa não pode estar vazio.",
        });
        setLoading(false);
        return;
      }

      // Se estiver alterando email
      if (emailForm.novoEmail || emailForm.confirmarEmail) {
        if (emailForm.novoEmail !== emailForm.confirmarEmail) {
          setMessage({ type: "error", text: "Os emails não coincidem." });
          setLoading(false);
          return;
        }

        if (emailForm.novoEmail === emailForm.emailAtual) {
          setMessage({
            type: "error",
            text: "O novo email deve ser diferente do atual.",
          });
          setLoading(false);
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailForm.novoEmail)) {
          setMessage({
            type: "error",
            text: "Por favor, digite um email válido.",
          });
          setLoading(false);
          return;
        }
      }

      // Se estiver alterando senha
      if (senhaForm.novaSenha || senhaForm.confirmarSenha) {
        if (!senhaForm.senhaAtual) {
          setMessage({
            type: "error",
            text: "Digite a senha atual para alterar a senha.",
          });
          setLoading(false);
          return;
        }

        if (senhaForm.novaSenha !== senhaForm.confirmarSenha) {
          setMessage({ type: "error", text: "As senhas não coincidem." });
          setLoading(false);
          return;
        }

        if (senhaForm.novaSenha.length < 6) {
          setMessage({
            type: "error",
            text: "A nova senha deve ter pelo menos 6 caracteres.",
          });
          setLoading(false);
          return;
        }

        if (senhaForm.novaSenha === senhaForm.senhaAtual) {
          setMessage({
            type: "error",
            text: "A nova senha deve ser diferente da atual.",
          });
          setLoading(false);
          return;
        }
      }

      // Atualizar dados básicos da empresa
      const telefoneNumerico = telefoneEmpresa.replace(/\D/g, "");

      // Validar telefone
      if (telefoneNumerico && telefoneNumerico.length < 10) {
        setMessage({
          type: "error",
          text: "Telefone inválido. Deve ter pelo menos 10 dígitos.",
        });
        setLoading(false);
        return;
      }

      const dadosAtualizacao: any = {
        nome: nomeEmpresa.trim(),
        telefone: telefoneEmpresa || "", // Mantém formatação do telefone
        // endereco não é um campo direto da empresa, é uma tabela separada
        // seria necessário atualizar via idEndereco se quiser mudar
      };

      // Adicionar email se estiver alterando
      if (emailForm.novoEmail && emailForm.novoEmail !== emailForm.emailAtual) {
        dadosAtualizacao.email = emailForm.novoEmail;
      }

      // Adicionar senha se estiver alterando (com validação de senha atual)
      if (senhaForm.novaSenha && senhaForm.senhaAtual) {
        dadosAtualizacao.senha = senhaForm.novaSenha;
        dadosAtualizacao.senhaAtual = senhaForm.senhaAtual; // Backend vai validar
      }

      // Chamar API de atualização
      await atualizarEmpresa(empresa.id, dadosAtualizacao);

      // Se houver imagem selecionada, salvar também
      if (imagemSelecionada && previewImagem) {
        try {
          const urlImagem = previewImagem;

          // Salvar no backend usando o endpoint específico PUT /empresas/{id}/imagem
          await atualizarImagemEmpresa(empresa.id, urlImagem);

          // Salvar no localStorage
          localStorage.setItem(`empresaImagem_${empresa.id}`, urlImagem);
          localStorage.removeItem("empresaImagem");

          // Atualizar estado local da imagem
          setEmpresa({ ...empresa, imagem: urlImagem });
          setImagemSelecionada(null);

          // Atualizar preview com URL do backend
          const urlImagemBackend = `${getImagemEmpresaUrl(
            empresa.id,
          )}?t=${Date.now()}`;
          setPreviewImagem(urlImagemBackend);

          // Disparar evento para atualizar painel de vagas
          window.dispatchEvent(
            new CustomEvent("empresaImagemAtualizada", {
              detail: { empresaId: empresa.id, novaImagem: urlImagem },
            }),
          );
        } catch (imagemError) {
          console.error("Erro ao salvar imagem:", imagemError);
          // Continua mesmo se a imagem falhar
        }
      }

      // Atualizar estado local
      setEmpresa({
        ...empresa,
        nome: nomeEmpresa.trim(),
        telefone: telefoneEmpresa || "",
        // endereco mantido do estado atual
        email: emailForm.novoEmail || empresa.email,
      });

      // Limpar campos de senha e email
      if (emailForm.novoEmail) {
        setEmailForm({
          emailAtual: emailForm.novoEmail,
          novoEmail: "",
          confirmarEmail: "",
        });
      }

      setSenhaForm({
        senhaAtual: "",
        novaSenha: "",
        confirmarSenha: "",
      });

      setMessage({
        type: "success",
        text: imagemSelecionada
          ? "Alterações e imagem salvas com sucesso!"
          : "Alterações salvas com sucesso!",
      });
    } catch (error: any) {
      console.error("Erro ao salvar alterações:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Erro ao salvar alterações. Tente novamente.";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!empresa) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Dados da empresa não encontrados.
          <Button onClick={() => navigate("/login-empresa")} sx={{ ml: 2 }}>
            Fazer Login
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <SettingsIcon sx={{ fontSize: 32, mr: 2, color: "primary.main" }} />
          <Typography variant="h5" component="h1" fontWeight="600">
            Configurações da Empresa
          </Typography>
        </Box>

        {/* Informações da Empresa */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Informações da Empresa"
            titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ pt: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              <TextField
                label="Nome da Empresa"
                value={nomeEmpresa}
                onChange={(e) => setNomeEmpresa(e.target.value)}
                required
                fullWidth
                variant="outlined"
              />
              <TextField
                label="CNPJ"
                value={formatarCNPJ(empresa.cnpj)}
                disabled
                fullWidth
                variant="outlined"
                helperText="CNPJ não pode ser alterado"
              />
              <TextField
                label="Telefone"
                value={telefoneEmpresa}
                onChange={(e) =>
                  setTelefoneEmpresa(formatarTelefone(e.target.value))
                }
                fullWidth
                variant="outlined"
                placeholder="(51) 99999-9999"
              />
              <TextField
                label="Email"
                value={emailForm.emailAtual}
                disabled
                fullWidth
                variant="outlined"
                helperText="Para alterar o email, preencha os campos abaixo"
              />
            </Box>
          </CardContent>
        </Card>

        {message.text && (
          <Alert severity={message.type as any} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexDirection: { xs: "column", lg: "row" },
          }}
        >
          {/* Alterar Email */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                title="Alterar Email"
                avatar={<EmailIcon color="primary" />}
                titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Email Atual"
                    name="emailAtual"
                    type="email"
                    value={emailForm.emailAtual}
                    disabled
                    fullWidth
                    variant="outlined"
                  />

                  <TextField
                    label="Novo Email"
                    name="novoEmail"
                    type="email"
                    value={emailForm.novoEmail}
                    onChange={handleEmailChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Digite o novo email"
                  />

                  <TextField
                    label="Confirmar Novo Email"
                    name="confirmarEmail"
                    type="email"
                    value={emailForm.confirmarEmail}
                    onChange={handleEmailChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Confirme o novo email"
                    error={
                      emailForm.confirmarEmail !== "" &&
                      emailForm.novoEmail !== emailForm.confirmarEmail
                    }
                    helperText={
                      emailForm.confirmarEmail !== "" &&
                      emailForm.novoEmail !== emailForm.confirmarEmail
                        ? "Os emails não coincidem"
                        : "Deixe em branco para manter o email atual"
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Alterar Senha */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: "100%" }}>
              <CardHeader
                title="Alterar Senha"
                avatar={<LockIcon color="primary" />}
                titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Senha Atual"
                    name="senhaAtual"
                    type={mostrarSenhaAtual ? "text" : "password"}
                    value={senhaForm.senhaAtual}
                    onChange={handleSenhaChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Digite a senha atual"
                    helperText="Necessário para alterar a senha"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setMostrarSenhaAtual(!mostrarSenhaAtual)
                            }
                            edge="end"
                          >
                            {mostrarSenhaAtual ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Nova Senha"
                    name="novaSenha"
                    type={mostrarNovaSenha ? "text" : "password"}
                    value={senhaForm.novaSenha}
                    onChange={handleSenhaChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Digite a nova senha"
                    inputProps={{ minLength: 6 }}
                    helperText="Mínimo 6 caracteres"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setMostrarNovaSenha(!mostrarNovaSenha)
                            }
                            edge="end"
                          >
                            {mostrarNovaSenha ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Confirmar Nova Senha"
                    name="confirmarSenha"
                    type={mostrarConfirmarSenha ? "text" : "password"}
                    value={senhaForm.confirmarSenha}
                    onChange={handleSenhaChange}
                    fullWidth
                    variant="outlined"
                    placeholder="Confirme a nova senha"
                    error={
                      senhaForm.confirmarSenha !== "" &&
                      senhaForm.novaSenha !== senhaForm.confirmarSenha
                    }
                    helperText={
                      senhaForm.confirmarSenha !== "" &&
                      senhaForm.novaSenha !== senhaForm.confirmarSenha
                        ? "As senhas não coincidem"
                        : "Deixe em branco para manter a senha atual"
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setMostrarConfirmarSenha(!mostrarConfirmarSenha)
                            }
                            edge="end"
                          >
                            {mostrarConfirmarSenha ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Gerenciar Imagem da Empresa */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Imagem da Empresa"
            avatar={<ImageIcon color="primary" />}
            titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ pt: 1 }}>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {/* Preview da Imagem - Tamanho do painel de vagas */}
              <Box
                sx={{
                  width: 212, // 250 - 15% = 212.5, arredondado para 212
                  height: 212, // Mantém quadrado para o círculo
                  border: "2px dashed #ccc",
                  borderRadius: "50%", // Circular
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                  overflow: "hidden",
                  flexShrink: 0,
                  margin: "0 auto", // Centralizar
                }}
              >
                {previewImagem ? (
                  <img
                    src={previewImagem}
                    alt="Preview da imagem"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                ) : (
                  <Box sx={{ textAlign: "center", color: "#999" }}>
                    <PhotoCameraIcon sx={{ fontSize: 32, mb: 0.5 }} />
                    <Typography variant="caption">Nenhuma imagem</Typography>
                  </Box>
                )}
              </Box>

              {/* Botões e Info */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: { xs: "center", sm: "flex-start" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={<PhotoCameraIcon />}
                    disabled={loading}
                  >
                    Selecionar
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImagemChange}
                    />
                  </Button>

                  {imagemSelecionada && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={handleSalvarImagem}
                      disabled={loading}
                    >
                      {loading ? "Salvando..." : "Salvar"}
                    </Button>
                  )}
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    JPEG, PNG, GIF, WebP - Máx. 5MB
                  </Typography>
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                      display: "block",
                      fontWeight: 500,
                    }}
                  >
                    ✓ Redimensionamento inteligente
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Botão Salvar Alterações */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={<SaveIcon />}
            onClick={handleSalvarAlteracoes}
            sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
          >
            {loading ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </Box>
      </Paper>

      <Box
        sx={{
          mt: 3,
          pb: 2,
        }}
      >
        <TechSoftFooter />
      </Box>
    </Container>
  );
};

export default ConfiguracoesEmpresa;
